import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import {
  Plus,
  Trash2,
  Save,
  ChevronLeft,
  Search,
  Maximize2,
  Minimize2,
  FilePlus,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AVAILABLE_CONTRACT_ITEMS } from "./Contract";
import { generatePDF } from "./GeneratePDF";

export default function MBCreateOrEdit() {
  const navigate = useNavigate();
  const { id } = useParams(); // Reads the ID string parameter directly out of the URL string layout
  const isEditMode = !!id;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [billData, setBillData] = useState({
    site_id: "",
    bill_no: "",
    po_number: "",
    date: new Date().toISOString().split("T")[0],
    items: [],
  });

  // Initial runtime environment sync dependencies
  useEffect(() => {
    fetchSites();
    if (isEditMode) {
      fetchExistingMB();
    }
  }, [id]);

  async function fetchSites() {
    const { data } = await supabase.from("sites").select("id, site_name");
    if (data) setSites(data);
  }

  // Fetch the full original item state tree if editing
  async function fetchExistingMB() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("measurement_bills")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        setBillData({
          site_id: data.site_id || "",
          bill_no: data.bill_no || "",
          po_number: data.po_number || "",
          date: data.date || "",
          items: data.items || [], // Restores exact structural dynamic array states natively
        });
      }
    } catch (err) {
      console.error("Error loading historical profile data record: ", err);
      toast.error("Failed to recover target specification sheets.");
      navigate("/mb");
    } finally {
      setLoading(false);
    }
  }

  const addContractItem = (item) => {
    const isDuplicate = billData.items.some(
      (existingItem) => existingItem.tender_line === item.tender_line,
    );

    if (isDuplicate) {
      return toast.error(
        `Line ${item.tender_line} is already added to this sheet.`,
      );
    }
    const newItem = {
      tender_line: item.tender_line || "N/A",
      description: item.description || "",
      unit: item.uom || "M3",
      rate: Number(item.rate) || 0,
      total_qty: 0,
      amount: 0,
      isCustom: false,
      measurements: [
        {
          sub_description: "Initial",
          qty_nos: 1,
          length: 0,
          width: 0,
          height: 0,
          total: 0,
        },
      ],
    };
    setBillData({ ...billData, items: [...billData.items, newItem] });
    setSearchTerm("");
    toast.success(`Line ${item.tender_line} added`);
  };

  const addCustomBlankItem = () => {
    const newItem = {
      tender_line: "01",
      description: "",
      unit: "M3",
      rate: 0,
      total_qty: 0,
      amount: 0,
      isCustom: true,
      measurements: [
        {
          sub_description: "Initial",
          qty_nos: 1,
          length: 0,
          width: 0,
          height: 0,
          total: 0,
        },
      ],
    };
    setBillData({ ...billData, items: [...billData.items, newItem] });
    toast.success("Blank custom row added");
  };

  const removeItem = (index) => {
    const newItems = billData.items.filter((_, i) => i !== index);
    setBillData({ ...billData, items: newItems });
    toast.success("Item removed");
  };

  const addSubRow = (itemIndex) => {
    const newItems = [...billData.items];
    newItems[itemIndex].measurements.push({
      sub_description: "",
      qty_nos: 1,
      length: 0,
      width: 0,
      height: 0,
      total: 0,
    });
    setBillData({ ...billData, items: newItems });
  };

  const updateParentItem = (index, field, value) => {
    const newItems = [...billData.items];
    if (field === "rate") {
      const numericRate = Number(value) || 0;
      newItems[index][field] = numericRate;
      newItems[index].amount = newItems[index].total_qty * numericRate;
    }else if (field === "total_qty") {
      const numericQty = Number(value) || 0;
      newItems[index][field] = numericQty;
      newItems[index].amount = numericQty * newItems[index].rate;
    } else {
      newItems[index][field] = value;
    }
    setBillData({ ...billData, items: newItems });
  };

  const updateMeasurement = (itemIndex, mIndex, field, value) => {
    const newItems = [...billData.items];
    const m = newItems[itemIndex].measurements[mIndex];
    m[field] = value;
    m.total =
      (Number(m.qty_nos) || 0) *
      (Number(m.length) || 1) *
      (Number(m.width) || 1) *
      (Number(m.height) || 1);

    const itemTotalQty = newItems[itemIndex].measurements.reduce(
      (sum, curr) => sum + curr.total,
      0,
    );
    newItems[itemIndex].total_qty = itemTotalQty;
    newItems[itemIndex].amount = itemTotalQty * newItems[itemIndex].rate;
    setBillData({ ...billData, items: newItems });
  };

  const calculateGrandAmount = () =>
    billData.items.reduce((sum, item) => sum + item.amount, 0);

  // Dynamic Unified Save Handler handles both operations securely
  const handleSave = async () => {
    if (!billData.site_id) return toast.error("Select a site");
    if (!billData.bill_no) return toast.error("Enter bill number");
    if (billData.items.length === 0)
      return toast.error("Add at least one item");

    const hasInvalidCustomItem = billData.items.some(
      (item) => item.isCustom && !item.description.trim(),
    );
    if (hasInvalidCustomItem)
      return toast.error("Please enter a description for all custom items");

    setLoading(true);
    try {
      const payload = {
        ...billData,
        total_amount: calculateGrandAmount(),
      };

      let error;
      if (isEditMode) {
        // Run SQL update targeted directly at row ID constraints
        const response = await supabase
          .from("measurement_bills")
          .update(payload)
          .eq("id", id);
        error = response.error;
      } else {
        // Default pristine standard compilation insertion rule
        const response = await supabase
          .from("measurement_bills")
          .insert([payload]);
        error = response.error;
      }

      if (error) throw error;

      toast.success(isEditMode ? "Changes Saved Natively!" : "Saved!");
      generatePDF(billData, sites, calculateGrandAmount);
      navigate("/mb");
    } catch (err) {
      console.error("Database save transaction process rejected: ", err);
      toast.error(err.message || "Database Write Failure Profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen bg-[#F8FAFC] transition-all ${isFullscreen ? "p-0" : "p-6"}`}
    >
      <div className={`max-w-7xl mx-auto ${isFullscreen ? "max-w-full" : ""}`}>
        {/* Navigation & Actions */}
        <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-2xl border border-slate-200">
          <button
            onClick={() => navigate("/mb")}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="bg-slate-100 text-slate-600 px-4 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-200"
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              {isFullscreen ? "NORMAL VIEW" : "FULL SCREEN"}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="bg-[#0054A6] hover:bg-blue-700 disabled:bg-slate-400 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-blue-100 transition-colors"
            >
              <Save size={18} />{" "}
              {loading
                ? "SAVING..."
                : isEditMode
                  ? "UPDATE & GENERATE"
                  : "SAVE"}
            </button>
          </div>
        </div>

        {/* MB Details Card */}
        {!isFullscreen && (
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Site Name
              </label>
              <select
                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 font-bold outline-none focus:border-blue-500"
                value={billData.site_id}
                onChange={(e) =>
                  setBillData({ ...billData, site_id: e.target.value })
                }
              >
                <option value="">Select Site...</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.site_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Bill Number
              </label>
              <input
                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 font-bold"
                value={billData.bill_no}
                onChange={(e) =>
                  setBillData({ ...billData, bill_no: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                PO Number
              </label>
              <input
                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 font-bold"
                value={billData.po_number}
                onChange={(e) =>
                  setBillData({ ...billData, po_number: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Date
              </label>
              <input
                type="date"
                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 font-bold"
                value={billData.date}
                onChange={(e) =>
                  setBillData({ ...billData, date: e.target.value })
                }
              />
            </div>
          </div>
        )}

        {/* Calculation Table Area */}
        <div
          className={`bg-white rounded-[2.5rem] border border-slate-200 shadow-xl overflow-hidden ${isFullscreen ? "rounded-none border-none h-screen" : ""}`}
        >
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-12 pr-4 font-medium outline-none focus:border-blue-400"
                placeholder="Search contract items to add..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <div className="absolute top-full left-0 right-0 bg-white shadow-2xl border border-slate-200 rounded-xl mt-2 z-50 max-h-60 overflow-y-auto">
                  {AVAILABLE_CONTRACT_ITEMS.filter((item) => {
                    const search = searchTerm.trim().toLowerCase();

                    return [
                      item.description,
                      item.contract_line,
                      item.tender_line,
                    ]
                      .filter(Boolean)
                      .some((field) =>
                        field.toString().toLowerCase().includes(search),
                      );
                  }).map((item, index) => (
                    <div
                      key={index}
                      onClick={() => addContractItem(item)}
                      className="p-4 hover:bg-blue-50 cursor-pointer border-b last:border-0 flex justify-between gap-4"
                    >
                      <span className="text-slate-500 font-bold">
                        {item.tender_line}
                      </span>
                      <span className="font-bold text-slate-700 flex-1">
                        {item.description}
                      </span>
                      <span className="text-blue-600 font-black whitespace-nowrap">
                        ₹{item.rate}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={addCustomBlankItem}
              className="w-full sm:w-auto bg-[#0054A6]/10 hover:bg-[#0054A6]/15 text-[#0054A6] px-5 py-2.5 rounded-xl text-xs font-black tracking-wider uppercase flex items-center justify-center gap-2 transition-all shrink-0"
            >
              <FilePlus size={16} /> Add Custom Item
            </button>
          </div>

          <div className="overflow-auto max-h-[calc(100vh-250px)]">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr className="text-[10px] font-black text-slate-500 uppercase">
                  <th className="px-6 py-4 border-b">LINE</th>
                  <th className="px-6 py-4 border-b">ITEM DESCRIPTION</th>
                  <th className="px-4 py-4 border-b text-center">UNIT</th>
                  <th className="px-2 py-4 border-b text-center">NOS</th>
                  <th className="px-2 py-4 border-b text-center">L</th>
                  <th className="px-2 py-4 border-b text-center">W</th>
                  <th className="px-2 py-4 border-b text-center">H/D</th>
                  <th className="px-6 py-4 border-b text-right">QTY</th>
                  <th className="px-6 py-4 border-b text-right">RATE</th>
                  <th className="px-6 py-4 border-b text-right">AMOUNT</th>
                  <th className="px-4 py-4 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {billData.items.map((item, iIdx) => (
                  <React.Fragment key={iIdx}>
                    <tr className="bg-slate-50/80 font-bold border-t border-slate-200">
                      <td className="px-6 py-3">
                        <span
                          className={`px-2.5 py-1 text-[10px] rounded-lg font-black uppercase ${item.isCustom ? "bg-amber-100 text-amber-700" : "bg-slate-200/60 text-slate-600"}`}
                        >
                          {item.isCustom ? (
                            <input
                              type="text"
                              className="w-12 bg-transparent font-black"
                              value={item.tender_line}
                              onChange={(e) =>
                                updateParentItem(
                                  iIdx,
                                  "tender_line",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            item.tender_line
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs">
                        {item.isCustom ? (
                          <input
                            type="text"
                            value={item.description}
                            placeholder="Type custom work details description..."
                            onChange={(e) =>
                              updateParentItem(
                                iIdx,
                                "description",
                                e.target.value,
                              )
                            }
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 font-bold text-slate-800 outline-none focus:border-blue-400"
                          />
                        ) : (
                          item.description
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        {item.isCustom ? (
                          <input
                            type="text"
                            value={item.unit}
                            placeholder="UOM"
                            onChange={(e) =>
                              updateParentItem(iIdx, "unit", e.target.value)
                            }
                            className="w-16 text-center bg-white border border-slate-200 rounded-lg py-1.5 font-bold text-slate-700 uppercase outline-none"
                          />
                        ) : (
                          item.unit
                        )}
                      </td>
                      <td
                        colSpan={4}
                        className="text-right text-[10px] text-slate-400 px-6 uppercase italic"
                      >
                        Item Total
                      </td>
                     <td className="px-6 py-3 text-right">
  <input
    type="number"
    step="any"
    className="w-24 text-right bg-transparent text-slate-900 outline-none focus:bg-white border-b border-dashed border-slate-300 focus:border-solid focus:border-slate-400"
    value={item.total_qty}
    onChange={(e) =>
      updateParentItem(iIdx, "total_qty", e.target.value)
    }
  />
</td>
                      <td className="px-6 py-3 text-right">
                        <input
                          type="number"
                          className="w-20 text-right bg-transparent text-slate-600 outline-none focus:bg-white border-b border-dashed border-slate-300 focus:border-solid focus:border-slate-400"
                          value={item.rate}
                          onChange={(e) =>
                            updateParentItem(iIdx, "rate", e.target.value)
                          }
                        />
                      </td>
                      <td className="px-6 py-3 text-right text-[#0054A6]">
                        {item.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => removeItem(iIdx)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                    {item.measurements.map((m, mIdx) => (
                      <tr
                        key={mIdx}
                        className="hover:bg-blue-50/20 group border-b border-slate-50"
                      >
                        <td className="px-6 py-2">
                          {mIdx === 0 && (
                            <button
                              onClick={() => addSubRow(iIdx)}
                              className="text-blue-500 hover:scale-110 transition-transform"
                            >
                              <Plus size={16} />
                            </button>
                          )}
                        </td>
                        <td className="px-10 py-2">
                          <input
                            className="w-full bg-transparent text-[11px] italic outline-none border-b border-transparent focus:border-blue-300"
                            value={m.sub_description}
                            placeholder="Measurement detail..."
                            onChange={(e) =>
                              updateMeasurement(
                                iIdx,
                                mIdx,
                                "sub_description",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td></td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="number"
                            className="w-16 text-center text-xs border rounded p-1"
                            value={m.qty_nos}
                            onChange={(e) =>
                              updateMeasurement(
                                iIdx,
                                mIdx,
                                "qty_nos",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="number"
                            className="w-16 text-center text-xs border rounded p-1"
                            value={m.length}
                            onChange={(e) =>
                              updateMeasurement(
                                iIdx,
                                mIdx,
                                "length",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="number"
                            className="w-16 text-center text-xs border rounded p-1"
                            value={m.width}
                            onChange={(e) =>
                              updateMeasurement(
                                iIdx,
                                mIdx,
                                "width",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="number"
                            className="w-16 text-center text-xs border rounded p-1"
                            value={m.height}
                            onChange={(e) =>
                              updateMeasurement(
                                iIdx,
                                mIdx,
                                "height",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-6 py-2 text-right text-xs font-bold text-slate-500">
                          {m.total.toFixed(3)}
                        </td>
                        <td colSpan={3}></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-900 p-6 flex justify-between items-center text-white">
            <div>
              <span className="text-slate-400 text-[10px] font-black uppercase block mb-1">
                Grand Total Amount
              </span>
              <span className="text-3xl font-black text-blue-400">
                ₹{calculateGrandAmount().toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
